import { Check, ListChecks, Lock, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatNumberRo } from '@/lib/format';
import { remainingCount, firstUnresolvedStep, UNRESOLVED_STEP_IDS, WIZARD_STEPS } from '../lib/wizard';
import type { RunProgress } from '../lib/progress';
import { VerdictLine } from '../VerdictLine';
import { useBulkResolve } from '../api/reviewHooks';
import { useFinalizeRun } from '../api/runHooks';

export interface WizardSummaryProps {
  /** Per-bucket counts, keyed by `WizardBucket` (T1's `wizardBucket`). */
  counts: Record<string, number>;
  runId: number;
  /** T4: same `runProgress(rows)` totals MaparePage's card opens with — the
   *  wizard's own "sumar" step opens with the identical Romanian sentence. */
  progress: RunProgress;
  /** Row ids that HAVE a final classification but were never validated (pipeline
   *  auto-confirmed, never human-signed-off). „Validează tot ce a rămas"
   *  bulk-accepts exactly these so `validated` catches up to `classified`. */
  unvalidatedResolvedIds: number[];
  /** True on an already-finalized run — hides the „Finalizează maparea" action. */
  finalized: boolean;
}

/**
 * The „sumar" step of `WIZARD_STEPS` (T1). Originally a straight port of v1
 * `ValidationWizard.tsx`'s inline `WizardSummary` (Rule Zero), which always
 * showed the green checkmark above whatever text applied — including "N
 * cazuri rămân de revizuit" when N was in the hundreds. An Impeccable
 * critique flagged that as a peak-end contradiction (a success glyph over
 * unfinished work, backed only by a backward link) and the `rounded-2xl
 * bg-teal-wash` icon-badge treatment as a card-like object out of place in
 * Direction B's editorial document (R1 fix, P1).
 *
 * Two states now:
 * - `allValidated` (`remaining === 0` AND `progress.validated >=
 *   progress.classified` — review fix, see below): the earned celebratory
 *   state — check + `VerdictLine` (T4: the same „N validate din M
 *   clasificate" sentence MaparePage's own card opens with, replacing the
 *   old hardcoded "Toate cazurile au fost validate." here) + "Înapoi la
 *   tabel".
 * - otherwise: no success glyph. `VerdictLine` again (replacing the
 *   old wizard-specific "Rularea nu este completă…" line — the ledger rows
 *   right below already itemize what's left, more precisely), then an
 *   editorial progress ledger — a warn-colored 2px rule (DESIGN.md: „title:
 *   … warn-colored for review groups"), one hairline row per non-empty
 *   unresolved bucket (label from `WIZARD_STEPS`, RO-formatted tabular-nums
 *   count, right-aligned), a total-remaining line, and a forward CTA to the
 *   first non-empty unresolved step (`firstUnresolvedStep`) plus a quiet
 *   "Înapoi la tabel" secondary. „Înapoi la tabel" returns to the Tabel view
 *   of the same run (T5: `/mapari/:id/tabel`) rather than v1's `/jobs/:id`.
 */
export function WizardSummary({ counts, runId, progress, unvalidatedResolvedIds, finalized }: WizardSummaryProps) {
  const navigate = useNavigate();
  const bulkResolve = useBulkResolve(runId);
  const finalizeRun = useFinalizeRun();
  const nUnvalidated = unvalidatedResolvedIds.length;
  const validateRemaining = () =>
    nUnvalidated > 0 && bulkResolve.mutate({ ids: unvalidatedResolvedIds, resolution: 'accepted' });
  const finalize = () => finalizeRun.mutate({ id: runId, finalized: true });

  const remaining = remainingCount(counts);
  /** Review fix: `remaining === 0` alone is `wizardBucket`'s looser „handled"
   *  predicate (accepts a pipeline-set `reviewResolution`) — NOT the same as
   *  `progress.validated`'s stricter human-sign-off rule (`progress.ts`
   *  forbids reconciling the two). Gating the celebratory checkmark on BOTH
   *  is a display-only guard: without it, the checkmark could sit above a
   *  `VerdictLine` still reading "857 din 1189 validate" — the exact
   *  success-glyph-over-unfinished-work contradiction this slice exists to
   *  remove, reintroduced here. When they disagree, fall through to the
   *  non-celebratory ledger view below (honest, even if its ledger is empty).
   */
  const allValidated = remaining === 0 && progress.validated >= progress.classified;

  if (allValidated) {
    return (
      <div className="mx-auto max-w-lg py-10 text-center">
        <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl bg-teal-wash">
          <Check className="size-7 text-ok" aria-hidden />
        </div>
        <h2 className="text-[17px] font-semibold text-ink">Validare</h2>
        <div className="mt-2">
          <VerdictLine progress={progress} />
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          {!finalized && (
            <button
              type="button"
              onClick={finalize}
              disabled={finalizeRun.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-teal px-5 py-2.5 text-[13.5px] font-semibold text-teal-ink hover:bg-teal/90 disabled:opacity-60"
            >
              {finalizeRun.isPending ? <Loader2 className="size-4 motion-safe:animate-spin" /> : <Lock className="size-4" />}
              {finalizeRun.isPending ? 'Se finalizează…' : 'Finalizează maparea'}
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(`/mapari/${runId}/tabel`)}
            className={
              finalized
                ? 'inline-flex items-center gap-2 rounded-md bg-teal px-5 py-2.5 text-[13.5px] font-semibold text-teal-ink hover:bg-teal/90'
                : 'text-[13.5px] font-medium text-ink-3 hover:text-ink-2'
            }
          >
            Înapoi la tabel
          </button>
        </div>
      </div>
    );
  }

  const next = firstUnresolvedStep(counts);
  const ledgerRows = UNRESOLVED_STEP_IDS.map((id) => WIZARD_STEPS.find((s) => s.id === id)!).filter(
    (s) => (counts[s.id] ?? 0) > 0
  );

  return (
    <div className="max-w-lg py-6">
      <h2 className="text-[17px] font-semibold text-ink">Validare</h2>
      <div className="mt-1">
        <VerdictLine progress={progress} />
      </div>
      <div className="mt-3 border-t-2 border-warn" />
      <ul>
        {ledgerRows.map((s) => (
          <li
            key={s.id}
            className="flex items-center justify-between border-b border-rule-soft py-2 text-[13.5px]"
          >
            <span className="text-ink-2">{s.label}</span>
            <span className="tabular-nums font-semibold text-ink">{formatNumberRo(counts[s.id] ?? 0)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-1 flex items-center justify-between border-b border-rule pt-1 text-[13.5px]">
        <span className="font-medium text-ink">Total rămase</span>
        <span className="tabular-nums font-semibold text-warn">{formatNumberRo(remaining)}</span>
      </div>
      {nUnvalidated > 0 && (
        <p className="mt-4 text-[13.5px] leading-relaxed text-ink-3">
          <span className="font-semibold tabular-nums text-ink">{formatNumberRo(nUnvalidated)}</span>{' '}
          {nUnvalidated === 1 ? 'rând este clasificat automat, dar încă nu figurează ca validat' : 'rânduri sunt clasificate automat, dar încă nu figurează ca validate'}.
          Validează-le pentru a le închide.
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-4">
        {next && (
          <button
            type="button"
            onClick={() => navigate(`/mapari/${runId}/validare/${next.id}`)}
            className="inline-flex items-center gap-2 rounded-md bg-teal px-5 py-2.5 text-[13.5px] font-semibold text-teal-ink hover:bg-teal/90"
          >
            Continuă cu {next.label} ({formatNumberRo(counts[next.id] ?? 0)}) →
          </button>
        )}
        {nUnvalidated > 0 && (
          <button
            type="button"
            onClick={validateRemaining}
            disabled={bulkResolve.isPending}
            className={
              next
                ? 'inline-flex items-center gap-2 rounded-md border border-rule px-5 py-2.5 text-[13.5px] font-semibold text-ink hover:bg-wash disabled:opacity-60'
                : 'inline-flex items-center gap-2 rounded-md bg-teal px-5 py-2.5 text-[13.5px] font-semibold text-teal-ink hover:bg-teal/90 disabled:opacity-60'
            }
          >
            {bulkResolve.isPending ? <Loader2 className="size-4 motion-safe:animate-spin" /> : <ListChecks className="size-4" />}
            {bulkResolve.isPending ? 'Se validează…' : `Validează tot ce a rămas (${formatNumberRo(nUnvalidated)})`}
          </button>
        )}
        <button
          type="button"
          onClick={() => navigate(`/mapari/${runId}/tabel`)}
          className="text-[13.5px] font-medium text-ink-3 hover:text-ink-2"
        >
          Înapoi la tabel
        </button>
      </div>
    </div>
  );
}
